import { useEffect, useMemo, useState } from 'preact/hooks'
import { Search } from 'lucide-preact'
import { emailBlastApi } from '@/api/emailBlast'
import type { BlastRecipientDto, BlastRecipientsPageDto } from '@/types/emailBlast'
import { routes } from '@/utils/routes'
import { lowerSafe } from '@/utils/strings'
import { SectionHeading } from './blastDetailSections'
import { AnalyticsExportButton } from '@/components/dashboard/analytics/AnalyticsExportButton'

const PAGE_SIZE = 25

interface Props {
  workspaceId: string
  clientCompanyId: string
  campaignId: string
  webId: string
  blastId: string
}

/**
 * Recipient drill-down for the blast detail (ORB-384): a paged, searchable list of the blast's
 * recipients with their status, opens, clicks, and last click. Each recipient deep-links to its ORB-383
 * contact route (lead anchor preferred, else the audience FileImportRow anchor).
 */
export function BlastRecipientsSection({ workspaceId, clientCompanyId, campaignId, webId, blastId }: Props) {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [data, setData] = useState<BlastRecipientsPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Debounce the search box; reset to the first page whenever the term changes.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    emailBlastApi
      .recipients(workspaceId, blastId, { skip: page * PAGE_SIZE, take: PAGE_SIZE, search: search || undefined, signal: ctrl.signal })
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setData(res.data)
        else setError(res.error || 'Failed to load recipients')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, blastId, page, search])

  const total = data?.Total ?? 0
  const items = data?.Items ?? []
  const rangeStart = total === 0 ? 0 : page * PAGE_SIZE + 1
  const rangeEnd = Math.min(total, page * PAGE_SIZE + items.length)
  const hasPrev = page > 0
  const hasNext = (page + 1) * PAGE_SIZE < total

  const contactHref = useMemo(
    () => (rec: BlastRecipientDto): string | null => {
      const scoped = routes.scoped(clientCompanyId, workspaceId)
      if (rec.LeadId) return scoped.webContact(campaignId, webId, 'lead', rec.LeadId)
      if (rec.FileImportRowId) return scoped.webContact(campaignId, webId, 'audience', rec.FileImportRowId)
      return null
    },
    [clientCompanyId, workspaceId, campaignId, webId],
  )

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <SectionHeading>Recipients</SectionHeading>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              value={searchInput}
              onInput={e => setSearchInput((e.target as HTMLInputElement).value)}
              placeholder="Search recipients"
              aria-label="Search recipients"
              className="w-44 sm:w-56 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 pl-8 pr-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          {/* Exports the whole blast (ORB-450), independent of the paging/search above. */}
          <AnalyticsExportButton
            onExport={() => emailBlastApi.exportRecipients(workspaceId, blastId)}
            fallbackFilename={`broadcast-recipients-${blastId}.csv`}
          />
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
              <th className="px-4 py-2.5 font-semibold">Recipient</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 font-semibold text-right">Opens</th>
              <th className="px-4 py-2.5 font-semibold text-right">Clicks</th>
              <th className="hidden px-4 py-2.5 font-semibold sm:table-cell">Last click</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">Loading recipients…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {search ? 'No recipients match your search.' : 'No recipients yet.'}
              </td></tr>
            ) : (
              items.map(rec => <RecipientRow key={rec.Id} rec={rec} href={contactHref(rec)} />)
            )}
          </tbody>
        </table>
        <div className="flex items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-800 px-4 py-2.5 text-xs text-slate-500 dark:text-slate-400">
          <span>{total === 0 ? 'No recipients' : `${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={!hasPrev || loading}
              className="rounded border border-slate-200 dark:border-slate-700 px-2 py-1 font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasNext || loading}
              className="rounded border border-slate-200 dark:border-slate-700 px-2 py-1 font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

function RecipientRow({ rec, href }: { rec: BlastRecipientDto; href: string | null }) {
  // Opens/clicks show only once the row reached delivery; queued / scheduled / bounced (never-delivered)
  // rows read "—", not a misleading 0 (review: Copilot — !BouncedAtUtc alone treated undelivered as reached).
  const reached = !!(rec.DeliveredAtUtc || rec.FirstOpenedAtUtc || rec.FirstClickedAtUtc)
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-4 py-2.5">
        {href ? (
          <a href={href} className="font-medium text-primary-700 dark:text-primary-400 hover:underline">
            {rec.RecipientName || rec.RecipientEmail}
          </a>
        ) : (
          <span className="font-medium text-slate-800 dark:text-slate-200">{rec.RecipientName || rec.RecipientEmail}</span>
        )}
        {rec.RecipientName && <span className="block text-xs text-slate-400 dark:text-slate-500">{rec.RecipientEmail}</span>}
      </td>
      <td className="px-4 py-2.5"><StatusBadge status={rec.Status} /></td>
      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{reached ? rec.OpenCount.toLocaleString() : '—'}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{reached ? rec.ClickCount.toLocaleString() : '—'}</td>
      <td className="hidden px-4 py-2.5 text-slate-500 dark:text-slate-400 sm:table-cell">
        <span className="block max-w-[16rem] truncate" title={rec.LastClickedUrl || undefined}>{rec.LastClickedUrl || '—'}</span>
      </td>
    </tr>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone = (() => {
    const s = lowerSafe(status)
    if (s.includes('click') || s.includes('open')) return 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400'
    if (s.includes('bounce') || s.includes('fail') || s.includes('complain')) return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
    if (s.includes('schedul') || s.includes('queue')) return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  })()
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{status}</span>
}
